import React from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Textarea
} from '@heroui/react';

import type { ContactSectionPayload, RoleItem } from '@/types';

interface ContactSectionFormModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  formData: ContactSectionPayload;
  setFormData: React.Dispatch<React.SetStateAction<ContactSectionPayload>>;
  isSubmitting: boolean;
  onSubmit: () => Promise<void> | void;
  isEditMode: boolean;
  roleOptions: RoleItem[];
}

export function ContactSectionFormModal({
  isOpen,
  onOpenChange,
  formData,
  setFormData,
  isSubmitting,
  onSubmit,
  isEditMode,
  roleOptions
}: ContactSectionFormModalProps) {
  const [error, setError] = React.useState<string | null>(null);

  const handleClose = React.useCallback(() => {
    onOpenChange(false);
    setError(null);
  }, [onOpenChange]);

  const handleSubmit = React.useCallback(async () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    setError(null);
    await onSubmit();
  }, [formData.name, onSubmit]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={handleClose} size="lg" isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              {isEditMode ? 'Edit Contact Section' : 'Create Contact Section'}
            </ModalHeader>
            <ModalBody onKeyDown={handleKeyDown}>
              <div className="space-y-4">
                <Input
                  label="Name"
                  placeholder="e.g. Billing"
                  value={formData.name}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, name: value }))}
                  isRequired
                  isInvalid={Boolean(error)}
                  errorMessage={error ?? undefined}
                />

                <Textarea
                  label="Description"
                  placeholder="Optional description for admins"
                  minRows={3}
                  value={formData.description ?? ''}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, description: value }))}
                />

                <Select
                  label="Visible to roles"
                  placeholder="Select allowed roles (leave empty for all)"
                  selectionMode="multiple"
                  selectedKeys={new Set(formData.roles)}
                  onSelectionChange={(keys) => {
                    const value = Array.from(keys as Set<string>);
                    setFormData((prev) => ({ ...prev, roles: value }));
                  }}
                >
                  {roleOptions.map((role) => (
                    <SelectItem key={role.name}>{role.name}</SelectItem>
                  ))}
                </Select>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose} isDisabled={isSubmitting}>
                Cancel
              </Button>
              <Button color="primary" isLoading={isSubmitting} onPress={handleSubmit}>
                {isEditMode ? 'Update Section' : 'Create Section'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

